/**
 * Section 06's words, in one place.
 *
 * Authority: `07-configuration.md` Section 06 — the `Tunable Thresholds` table's labels and
 * hints are quoted from it verbatim, so the page and the spec cannot drift apart by a
 * paraphrase. The rule titles and descriptions are NOT here: they arrive from the API, which
 * reads them from the tournament's own `appointment_rule` rows, and a rule the TD added
 * mid-tournament has a title no constant in this repository could know.
 *
 * The change-log key vocabulary is here for the same reason in reverse: the log stores
 * `threshold.judge_sj_tj_tolerance` and `appointment_rule.country_conflict.enabled`, which
 * are exact and unreadable. Turning one into a sentence is presentation, and belongs beside
 * the labels it reuses rather than inside the panel that prints it.
 */

import type { AppointmentRuleView, ThresholdKey } from "../../api/types.js";

export interface ThresholdCopy {
  /** Section 06's own label. */
  label: string;
  /** Section 06's own hint. */
  hint: string;
  /**
   * What the keyboard should offer. `numeric` for a count of appointments or minutes; `text`
   * for ruling R-D6's backstop, which is a wall-clock time and not a number — a numeric keypad
   * over a field that wants `22:30` hides the colon it needs.
   */
  inputMode?: "numeric" | "text";
  /**
   * Which resolved figure the third column prints for this key, when the drafting engine is
   * not the thing that reads it. Ruling R-D6's two are read by the nightly report build, so a
   * cell saying `Not read by the technical engine` would be true and useless.
   */
  resolvedFrom?: "daily_report_delay" | "daily_report_backstop";
  /**
   * Whether the technical drafting engine actually reads this threshold.
   *
   * Three of the four are `EngineConfig` values. `umpire_onfield_per_day` is the umpire
   * side's and the technical engine does not take it — saying so on the row is the whole
   * point, because a field that looks like it governs the next draft and does not is exactly
   * the misreading this page exists to prevent.
   */
  engineReads: boolean;
}

const THRESHOLD_COPY: Readonly<Record<ThresholdKey, ThresholdCopy>> = {
  umpire_onfield_per_day: {
    label: "Umpire On-Field Duties Per Day",
    hint: "Video and reserve duties are counted separately and are not capped.",
    engineReads: false,
  },
  judge_sj_tj_tolerance: {
    label: "Judge SJ / TJ Balance Tolerance",
    hint: "Flag any judge whose two duty counts differ by more than this.",
    engineReads: true,
  },
  team_exposure_heat: {
    label: "Team Exposure Heat Threshold",
    hint: "Third appointment to the same team turns the matrix cell hot.",
    engineReads: true,
  },
  to_appointments_per_day: {
    label: "TO Appointments Per Day",
    hint: "Table officials may work more than once a day.",
    engineReads: true,
  },
  umpire_onfield_consecutive_days: {
    label: "Umpire Consecutive On-Field Days",
    hint: "The most consecutive calendar days an umpire may hold on-field duty.",
    engineReads: true,
  },
  /* ── Ruling R-D6 — when the daily report is built (migration 057) ────────── */
  daily_report_delay_minutes: {
    label: "Daily Report Delay After Last Match",
    hint:
      "Minutes after the last match of the day ends before the Consolidated Daily UM Debrief " +
      "Report is built. Blank uses 30.",
    engineReads: false,
    inputMode: "numeric",
    resolvedFrom: "daily_report_delay",
  },
  daily_report_backstop_time: {
    label: "Daily Report Backstop Time",
    hint:
      "The report never waits past this hour, however late the last match kicks off. " +
      "24-hour time, tournament time zone. Blank uses 22:30.",
    engineReads: false,
    inputMode: "text",
    resolvedFrom: "daily_report_backstop",
  },
  co_appointment_pair_tolerance: {
    label: "Co-Appointment Pair Tolerance",
    hint: "Flag any pair of officials sharing more than this many matches across the tournament.",
    // `engineReads` answers "does the number reach the engine's configuration", which this one
    // does: `engineConfig()` folds it like every tolerance beside it. It steers no draft — the
    // pair finding is advisory and computed outside the drafting engine — and the hint says
    // what it does instead, which is flag.
    engineReads: true,
  },
};

/**
 * The copy for one threshold key.
 *
 * A key this build has never heard of still renders — the API is the authority on which keys
 * are configurable, and a page that dropped an unknown one would hide a field the TD is
 * entitled to set. It gets its raw key as its label and no claim about the engine.
 */
export function thresholdCopy(key: string): ThresholdCopy {
  return (
    THRESHOLD_COPY[key as ThresholdKey] ?? {
      label: key,
      hint: "This threshold is served by the API and has no authored description in this build.",
      engineReads: false,
    }
  );
}

/** The `Country Conflict` rule, the one row that carries the `AI Drafts Treat As Hard` sub-toggle. */
export const COUNTRY_CONFLICT_RULE_CODE = "country_conflict";

/**
 * Whether this row gets the sub-toggle.
 *
 * Decided by the rule's code and NOT by `aiDraftsTreatAsHard` being non-null, because the
 * field is null on `Country Conflict` too until it has been set once. Keying off null would
 * hide the control on precisely the tournament that has never used it.
 */
export function hasAiHardnessSubToggle(rule: AppointmentRuleView): boolean {
  return rule.code === COUNTRY_CONFLICT_RULE_CODE;
}

/** The sub-toggle's position: stored where it has been set, otherwise the default, `On`. */
export function aiHardness(rule: AppointmentRuleView): boolean {
  return rule.aiDraftsTreatAsHard ?? true;
}

/** `Hard Block` / `Soft Warning` — Section 06's two badges. */
export function ruleKindLabel(kind: "hard" | "soft"): string {
  return kind === "hard" ? "Hard Block" : "Soft Warning";
}

export function ruleKindClass(kind: "hard" | "soft"): string {
  return kind === "hard" ? "cfgrule__kind cfgrule__kind--hard" : "cfgrule__kind cfgrule__kind--soft";
}

/** How many rules are in force, for the section's summary line. */
export function rulesInForce(rules: readonly AppointmentRuleView[]): number {
  return rules.filter((rule) => rule.enabled).length;
}

/**
 * A change-log key as a sentence: what moved, and — where the key names a rule the
 * tournament still carries — that rule's own title rather than its code.
 *
 * Unknown shapes are printed verbatim. The log is append-only and older than any release of
 * this page; a row it cannot parse is shown as stored, never swallowed.
 */
export function changeKeyLabel(key: string, rules: readonly AppointmentRuleView[]): string {
  const threshold = /^threshold\.(.+)$/.exec(key);
  if (threshold !== null) return `Threshold · ${thresholdCopy(threshold[1] ?? "").label}`;

  const rule = /^appointment_rule\.(.+)\.(enabled|ai_drafts_treat_as_hard)$/.exec(key);
  if (rule !== null) {
    const code = rule[1] ?? "";
    const title = rules.find((candidate) => candidate.code === code)?.title ?? code;
    const field = rule[2] === "enabled" ? "In Force" : "AI Drafts Treat As Hard";
    return `Rule · ${title} — ${field}`;
  }

  return key;
}

/**
 * A stored value as the page says it.
 *
 * The log stores booleans as the text the database wrote, so `true` reads as `On` where the
 * key is a switch and is left alone everywhere else. A value that was never set is not
 * "false" and not "0" — it is absent, and says so.
 */
export function changeValueLabel(value: string | null): string {
  if (value === null) return "Not set";
  if (value === "true") return "On";
  if (value === "false") return "Off";
  return value;
}
